import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import * as _ from 'lodash';
import { GemSpecies } from '../../utils/enums/gem-species.enum';
import { GemColor } from '../../utils/enums/gem-color.enum';
import { GemCut } from '../../utils/enums/gem-cut.enum';
import { GemShape } from '../../utils/enums/gem-shape.enum';
import { GemClarity } from '../../utils/enums/gem-clarity.enum';
import { GemTreatment } from '../../utils/enums/gem-treatment.enum';
import { GemOrigin } from '../../utils/enums/gem-origin.enum';
import { GemCertificate } from '../../utils/enums/gem-certification.enum';
import { SortOrder } from '../../utils/enums/sort-order.enum';
import { GemMediaType } from '../../utils/enums/gem-media-type.enum';
import { GemMediaScanType } from '../../utils/enums/gem-media-scan-type.enum';
import { AiStatus } from '../../utils/enums/gem-ai-status.enum';
import { GemBankType } from '../../utils/enums/gem-bank.enum';
import { Intensity } from '../../utils/enums/intensity.enum';
import { SpecialDescriptor } from '../../utils/enums/gem-special-descriptor.enum';
import { GemLocation } from '../../utils/enums/gem-location.enum';

export enum GemVaultType {
  GEM_VAULT = 'gemvault',
  MY_GEM_FAV = 'mygemfav',
  GEM_BANK = 'gembank',
}
export interface IGemMedia {
  media_id: string;
  location: string;
  mediaType: GemMediaType;
  gemId: string;
  scanType: GemMediaScanType;
  view: string;
}
export interface IGemAiResponse {
  responseId: string;
  colorPrediction: string;
  secondaryColorPrediction: string;
  specialColorPrediction: string;
  intensityPrediction: string;
  colorProbability: number;
  uginId?: string;
}
export interface IGem {
  gemId: string;
  systemId: string;
  uginId: string | null;
  is_scanned: boolean;
  createdAt: string;
  scannedDate: string | null;
  appearanceImage: string;
  favouriteId?: string;
  species: GemSpecies;
  weight: number;
  color: GemColor;
  intensity: Intensity | string;
  secondaryColor: GemColor | string;
  specialDescriptor: SpecialDescriptor | string;
  dimensions?: string;
  length: number;
  depth: number;
  width: number;
  cut: GemCut;
  shape: GemShape;
  aiStatus: AiStatus;
  clarity: GemClarity;
  treatment: GemTreatment;
  origin: GemOrigin;
  location: GemLocation | undefined;
  certificate: GemCertificate;
  media?: IGemMedia[];
  user?: string;
  organization?: string;
  organizationId?: string;
  aiResponse?: IGemAiResponse;
  passportLightGenerated?: boolean;
  aiTryAttempts?: number;
  isPublic?: boolean;
  version: string;
}

export interface IGemCreateFormValues {
  species: string;
  weight: string;
  color: string;
  intensity: string | undefined;
  secondaryColor: string;
  specialDescriptor: string;
  length: string;
  width: string;
  depth: string;
  cut: string;
  clarity: string;
  treatment: string;
  shape: string;
  origin: string;
  location: string | undefined;
  certificate: string;
  file: File | null;
  favouriteId?: string;
  isPublic?: boolean;
}

export interface IGemUpdateFormValues {
  species?: string;
  weight?: string;
  color?: string;
  intensity?: string | undefined;
  secondaryColor?: string;
  specialDescriptor?: string;
  length?: string;
  width?: string;
  depth?: string;
  cut?: string;
  clarity?: string;
  treatment?: string;
  shape?: string;
  origin?: string;
  location?: string | undefined;
  certificate?: string;
  file?: File | null;
  favouriteId?: string;
  isPublic?: boolean;
}

export interface IGemCreationPayload {
  appearanceImage?: string;
  appearanceImageContentType?: string;
  favouriteId?: string;
  species: string;
  weight: number;
  color: string;
  intensity: string | undefined;
  secondaryColor?: string;
  specialDescriptor?: string;
  length: number;
  depth: number;
  width: number;
  cut: string;
  clarity?: string;
  treatment?: string;
  shape: string;
  origin?: string;
  location?: string | undefined;
  certificate?: string;
  isPublic?: boolean;
  version?: string;
}

export interface IGemUpdatePayload {
  appearanceImage?: string;
  appearanceImageContentType?: string;
  favouriteId?: string;
  species?: string;
  weight?: number;
  color?: string;
  intensity?: string | undefined;
  secondaryColor?: string;
  specialDescriptor?: string;
  length?: number;
  depth?: number;
  width?: number;
  cut?: string;
  clarity?: string;
  treatment?: string;
  shape?: string;
  origin?: string;
  location?: string | undefined;
  certificate?: string;
  isPublic?: boolean;
}

export interface ISelectedFilterOption {
  [gemFeature: string]: string | string[];
}

export interface IGetGemsPayload {
  page: number;
  limit: number;
  sortBy?: string;
  sortOrder?: SortOrder;
  search?: string;
  selectedFilterOptions: ISelectedFilterOption[];
  type?: GemBankType;
}

export interface IGetGemByIdPayload {
  gemId: string;
  type?: GemBankType;
}

export interface IGemSlice {
  componentStatus: { [name: string]: undefined | string | boolean | number };
  gems: IGem[];
  totalGemsCount: number;
  favourites: string[];
  gem: IGem | null;
  gemToEdit: IGem | null;
  gemToOpen: IGem | null;
  selectedFilterOptions: ISelectedFilterOption[];
  isGemPassportLiteFirstTimeGenerated: boolean;
  lockedGemId: string | null;
}

const initialState: IGemSlice = {
  componentStatus: {},
  gemToEdit: null,
  gemToOpen: null,
  gems: [],
  totalGemsCount: 0,
  favourites: [],
  gem: null,
  selectedFilterOptions: [],
  isGemPassportLiteFirstTimeGenerated: false,
  lockedGemId: null,
};

const gemSlice = createSlice({
  name: 'gems',
  initialState,
  reducers: {
    setComponentStatus(
      state: IGemSlice,
      action: PayloadAction<{ [key: string]: undefined | string | boolean | number }>,
    ) {
      state.componentStatus = { ...state.componentStatus, ...action.payload };
    },
    clearComponentStatus(state: IGemSlice, action: PayloadAction<string[] | undefined>) {
      if (action.payload?.length) {
        state.componentStatus = _.omit(state.componentStatus, action.payload);
      } else {
        state.componentStatus = {};
      }
    },
    getGemsAsync(_state: IGemSlice, _action: PayloadAction<IGetGemsPayload>) {},
    getFavGemsAsync(_state: IGemSlice, _action: PayloadAction<IGetGemsPayload>) {},
    setGems(state: IGemSlice, action: PayloadAction<{ data: IGem[]; count: number }>) {
      state.gems = [...action.payload.data];
      state.totalGemsCount = action.payload.count;
    },
    setFavGems(state: IGemSlice, action: PayloadAction<{ data: IGem[]; count: number }>) {
      state.gems = [...action.payload.data];
      state.totalGemsCount = action.payload.count;
    },
    resetGetGems(state: IGemSlice) {
      state.gems = [];
      state.totalGemsCount = 0;
      state.gemToEdit = null;
      state.gemToOpen = null;
    },
    addRemoveFavouriteGemAsync(_state: IGemSlice, _action: PayloadAction<{ gemId: string; favouriteId?: string }>) {},
    togglePublicGemAsync(_state: IGemSlice, _action: PayloadAction<{ gemId: string }>) {},
    setFavoritesGems(state: IGemSlice, action: PayloadAction<string[]>) {
      state.favourites = [...state.favourites, ...action.payload];
    },
    setFavourites(state: IGemSlice, action: PayloadAction<string>) {
      state.favourites = state.favourites.includes(action.payload)
        ? state.favourites.filter((id) => id !== action.payload)
        : [...state.favourites, action.payload];
    },
    resetFavourites(state: IGemSlice) {
      state.favourites = [];
    },
    setGemPassportLiteFirstTimeGenerated: (state, action) => {
      state.isGemPassportLiteFirstTimeGenerated = action.payload;
    },
    generateGemPassportAsync(_state: IGemSlice, _action: PayloadAction<{ gemId: string }>) {},
    generateGemPassportLightAsync(_state: IGemSlice, _action: PayloadAction<{ gemId: string }>) {},
    getGemByIdAsync(_state: IGemSlice, _action: PayloadAction<IGetGemByIdPayload>) {},
    setGem(state: IGemSlice, action: PayloadAction<IGem>) {
      if (action.payload.favouriteId) {
        state.favourites.push(action.payload.gemId);
      }
      state.gem = action.payload;
    },
    setGemToEdit(state: IGemSlice, action: PayloadAction<IGem>) {
      state.gemToEdit = action.payload;
    },
    setGemToOpen(state: IGemSlice, action: PayloadAction<IGem>) {
      state.gemToOpen = action.payload;
    },
    openEditGemConfirmationModel(_state: IGemSlice, _action: PayloadAction<{ gem: IGem }>) {},
    openEditGemModel(_state: IGemSlice, _action: PayloadAction<{ gem: IGem }>) {},
    openAddGemModel(state: IGemSlice) {
      state.componentStatus.isAddGemModelOpen = true;
    },
    closeGemModel(state: IGemSlice, _action: PayloadAction<{ gemId: string | null }>) {
      state.gemToEdit = null;
    },
    openDeleteGemModel(state: IGemSlice) {
      state.componentStatus.isDeleteGemModelOpen = true;
    },
    closeDeleteGemModel(state: IGemSlice) {
      state.componentStatus.isDeleteGemModelOpen = false;
    },
    addGemAsync(
      _state: IGemSlice,
      _action: PayloadAction<{ gem: IGemCreateFormValues; image: File; type?: GemVaultType }>,
    ) {},
    editGemAsync(
      _state: IGemSlice,
      _action: PayloadAction<{
        gem: IGemUpdateFormValues;
        gemId: string;
        type?: GemVaultType;
        currentPage: number;
        currentLimit: number;
      }>,
    ) {},
    deleteGemAsync(_state: IGemSlice, _action: PayloadAction<{ gemId: string; type?: GemVaultType }>) {},
    setSelectedFilterOptions(state: IGemSlice, action: PayloadAction<ISelectedFilterOption[]>) {
      state.selectedFilterOptions = action.payload;
    },
    resetSelectedFilterOptions(state: IGemSlice) {
      state.selectedFilterOptions = [];
    },
    updateGemUginIdAsync(state: IGemSlice, _action: PayloadAction<{ uginId?: string }>) {
      if (state.gem) {
        state.gem.uginId = _action.payload.uginId ? _action.payload.uginId : null;
      }
      if (state.gem?.aiResponse) {
        state.gem.aiResponse.uginId = _action.payload.uginId;
      }
    },
    setUginIdAsync(state: IGemSlice, _action: PayloadAction<{ uginId: string }>) {
      if (state.gem) {
        state.gem.uginId = _action.payload.uginId;
        state.gem.aiResponse!.uginId = _action.payload.uginId;
      }
    },
    checkGemLockStatus(_state: IGemSlice, _action: PayloadAction<{ gemId: string }>) {},
    setLockedGem(state, action: PayloadAction<{ gemId: string }>) {
      state.lockedGemId = action.payload.gemId;
    },
    resetLockedGem(state) {
      state.lockedGemId = null;
    },
  },
});

export const { actions: gemActions, reducer: gemReducer } = gemSlice;
